import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Zoom de proyección (pedido del owner: vistas interactivas — clic para leer
 * el contenido completo). Overlay a pantalla llena sobre la Pantalla: la
 * tarjeta/punto elegido se lee a sala entera; clic en cualquier lado o Esc
 * cierra. Sin navegación ni controles extra: es una lupa, no un menú.
 */
export function Ampliada({
  abierta,
  onCerrar,
  children,
}: {
  abierta: boolean;
  onCerrar: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!abierta) return;
    const onTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', onTecla);
    return () => window.removeEventListener('keydown', onTecla);
  }, [abierta, onCerrar]);

  if (!abierta) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCerrar}
      /* El velo entra por su cuenta y la lámina crece un punto desde el
         centro: la lupa se materializa en vez de aparecer de golpe. Aparecer
         sin recorrido obliga a la vista a reencontrar dónde estaba mirando;
         medio segundo de escala se lo dice sin texto. Con movimiento reducido
         la regla base deja ambas en un parpadeo. */
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/90 px-8 py-10 motion-safe:animate-[velo_var(--transition-base)_var(--ease-salida)]"
    >
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar"
        className="absolute right-6 top-6 flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-inverse opacity-60 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <X className="h-8 w-8" aria-hidden="true" />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-full w-full max-w-5xl overflow-y-auto rounded-xl border border-border-strong bg-surface-raised p-10 shadow-lg motion-safe:animate-[lamina_var(--transition-lenta)_var(--ease-salida)]"
      >
        {children}
      </div>
    </div>
  );
}
